import {
  LockOutlined,
  CheckOutlined,
  LogoutOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import { Input, Button, Spin, notification, Form, Row, Alert } from "antd";
import { Rule } from "antd/lib/form";
import { useState, useCallback, FC } from "react";

import { useLoc, useSdk, useAuth } from "@app/shared/lib/hooks";
import { useUIAppState } from "@app/shared/ui/UIAppState/UIAppStateProvider";

type Props = {
  onSave?: () => void;
};

export const StudentPasswordForm: FC<Props> = ({ onSave }) => {
  const loc = useLoc();
  const sdk = useSdk();
  const [form] = Form.useForm();
  const UIAppState = useUIAppState();
  const { logout } = useAuth();

  const [password, setPassword] = useState("");
  const [saving, setSaving] = useState(false);

  const passwordRules: Rule[] = [
    {
      required: UIAppState.needChangePassword,
      message: loc.word("Required field"),
    },
    {
      type: "string",
      min: 6,
      message: loc.word("Password must be longer than 6 characters"),
    },
    {
      type: "string",
      max: 30,
      message: loc.word("Password must be shorter than 30 characters"),
    },
  ];

  const passwordConfirmRules: Rule[] = [
    {
      validator: async (_rule, value) => {
        if (password !== value)
          throw new Error(loc.word("Your passwords must match"));
      },
    },
  ];

  const save = useCallback(
    async (values: { password: string }) => {
      setSaving(true);
      try {
        const result = await sdk.api.account.updatePassword(values.password);
        if (result.success) {
          onSave?.();
          UIAppState.setNeedChangePassword(false);
          notification.success({
            message: loc.word("Student.passwordSaveSuccessMsg", {
              default: "Password has been successfully updated",
            }),
            duration: 2,
          });
        }
      } finally {
        setSaving(false);
      }
    },
    [UIAppState, loc, onSave, sdk.api.account],
  );

  return (
    <>
      <Alert
        type="warning"
        icon={<InfoCircleOutlined />}
        showIcon
        message={loc.word("StudentProfileForm.changePasswordHelp", {
          default:
            "You need to set a new password to continue using this platform",
        })}
        className="mb-10"
      />

      <Spin tip={loc.word("Loading")} spinning={saving}>
        <Form form={form} onFinish={save} layout="vertical">
          <Form.Item
            label={loc.word("Password")}
            name="password"
            rules={passwordRules}
          >
            <Input.Password
              prefix={<LockOutlined />}
              type="password"
              placeholder={loc.word("Enter your password")}
              onChange={(e) => setPassword(e.target.value)}
            />
          </Form.Item>
          <Form.Item
            label={loc.word("Confirm password")}
            name="confirmPassword"
            rules={passwordConfirmRules}
          >
            <Input.Password
              prefix={<LockOutlined />}
              type="password"
              placeholder={loc.word("Enter password confirmation")}
            />
          </Form.Item>
        </Form>
      </Spin>
      <footer>
        <Row justify="space-between">
          <Button danger icon={<LogoutOutlined />} onClick={logout}>
            {loc.word("Logout")}
          </Button>

          <Button
            type="primary"
            icon={<CheckOutlined />}
            loading={saving}
            onClick={() => form.submit()}
          >
            {loc.word("Save")}
          </Button>
        </Row>
      </footer>
    </>
  );
};
